////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.2                                                            //
//  Summary: Global configuration — env, theme, viewport, chrome assets      //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

// THE ONLY module in this application that reads `import.meta.env` (1.0.1.md §3.3,
// enforced by the acceptance-criteria grep and the eslint override below — both key
// on the filename `config.tsx`). Everything else imports `cfg` (env-only) or
// `useConfig()` (the full runtime config). Every VITE_-prefixed value is inlined into
// the built browser bundle, so nothing that lands in here may ever be a secret.
//
// MARK — this file used to be config.ts (env only). It has been merged, rather than
// left as a second config.tsx alongside it, because `@/config` can only resolve to
// ONE file — having both on disk is an ambiguous-resolution bug waiting to happen,
// not a stylistic choice. Flagging the merge for ratification since it wasn't asked
// for verbatim, but the alternative (a colliding filename) is worse.
//
// Pattern adapted from WOMBAT's gui/app/src/config.tsx (ConfigProvider/useConfig),
// with two deliberate departures agreed with Mike:
//   1. Asset theme rewrite only touches a structured `-(dark|light)` token
//      immediately before the file extension — never a bare string replace (site
//      content includes paths like ".../FirstLightInteriorWeb2_landscape_large.jpg"
//      that a naive `/dark|light/` replace would corrupt).
//   2. The palette is CSS custom properties on `:root`/`[data-theme]`
//      (styles/tokens.css) rather than values pushed into inline styles from JS —
//      Tailwind's utilities and the provider's `cfg.styles.palette` read the same
//      cascade instead of two copies of the same colours.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Env-derived, build-time config (unchanged shape/contract from the old config.ts)
// ---------------------------------------------------------------------------

interface EnvConfig {
  api: {
    // '' resolves to a same-origin relative base ('/api/v1') — correct for both the
    // dev proxy (vite.config.ts server.proxy) and the production build, where Caddy
    // and the API sit behind the same edge origin. Only set VITE_API_URL when the
    // GUI is deliberately pointed at a different origin (e.g. a preview deploy).
    base: string;
    timeout_ms: number;
  };
}

const cfg: EnvConfig = {
  api: {
    base: `${import.meta.env.VITE_API_URL ?? ''}/api/v1`,
    // Every request through lib/api.ts carries this timeout (Express 5 baseline,
    // 1.0.1.md §2.5, mirrors the API side of the same contract) — no request/response
    // cycle is allowed to hang the UI indefinitely.
    timeout_ms: 15000,
  },
};

/** Env-only config — lib/api.ts's axios instance is the sole consumer. Kept as the
 * default export for backwards compatibility with the pre-merge `config.ts`. */
export default cfg;

// ---------------------------------------------------------------------------
// Runtime config — theme, viewport, locale, chrome assets
// ---------------------------------------------------------------------------

export type Theme = 'light' | 'dark';
export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

export interface Palette {
  ink: string;
  paper: string;
  muted: string;
  line: string;
}

export interface Viewport {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  orientation: 'portrait' | 'landscape';
  touch: boolean;
}

export interface Capabilities {
  touch: boolean;
}

export interface NavigatorInfo {
  userAgent: string;
  platform: string;
  mobile: boolean;
}

export interface Locale {
  language: string;
  timezone: { name: string; offset: number };
}

/** One `<BackgroundLayer />` entry (1.0.3 post-review tweak #4). `src` is the
 * light-theme filename form — `withTheme()` rewrites it per-theme exactly as it
 * always has. `treated` is the new bit: whether BackgroundLayer's CSS
 * grayscale/tint/darken placeholder treatment (1.0.3.md §5's "existing scrape
 * imagery, treated in CSS") still applies to this entry.
 *   - `treated: true` — an untouched `/scrape/**` placeholder; BackgroundLayer
 *     keeps applying the full grayscale+contrast+brightness+tint CSS treatment
 *     so it reads as a monochrome architectural shot despite being an
 *     untouched colour photo.
 *   - `treated: false` — a real, pre-treated `bgi-<page>-{light,dark}.webp`
 *     pair Mike has supplied (already monochrome). BackgroundLayer renders it
 *     as supplied — no grayscale/contrast/brightness filter — keeping only the
 *     theme-aware ink tint overlay every entry gets, since that overlay is the
 *     text-contrast mechanism PageHero's white-text-over-dark-photo treatment
 *     assumes (see PageHero.tsx's own MARK), not part of the placeholder
 *     "make a colour scrape photo read as monochrome" trick. */
export interface BackgroundEntry {
  src: string;
  treated: boolean;
}

/** Per-route `<BackgroundLayer />` imagery (1.0.3.md §5), keyed to match
 * `components/chrome/BackgroundLayer.tsx`'s `PageKey`. Real assets land as
 * `bgi-<page>-{light,dark}.webp` (§5) — `withTheme()` rewrites those
 * automatically once they exist on disk (see `BackgroundEntry.src`'s doc
 * comment for `withTheme()`'s no-op behaviour against the remaining
 * `/scrape/**` placeholders, which carry no `-light`/`-dark` token). */
export interface BackgroundPaths {
  about: BackgroundEntry;
  careers: BackgroundEntry;
  contact: BackgroundEntry;
  home: BackgroundEntry;
  legal: BackgroundEntry;
  news: BackgroundEntry;
  projects: BackgroundEntry;
}

export interface AssetPaths {
  logo: { hoz: string };
  icon: { menu: string; search: string };
  background: BackgroundPaths;
}

export interface AppConfig {
  api: {
    base: string;
    url: string;
    timeout: number;
    headers: Record<string, string>;
  };
  app: {
    displayName: string;
    legalName: string;
    tagline: string;
    contact: { email: string; phone: string; address: string };
    legal: { privacyCookiesTerms: string };
    copyright: string;
  };
  assets: AssetPaths;
  client: {
    base: string;
    breakpoints: Record<Breakpoint, number>;
    capabilities: Capabilities;
    locale: Locale;
    navigator: NavigatorInfo;
    theme: Theme;
    title: string;
    viewport: Viewport;
  };
  styles: {
    palette: Palette;
  };
}

const BREAKPOINTS: Record<Breakpoint, number> = { sm: 640, md: 768, lg: 1024, xl: 1280 };

// Chrome assets in their light-theme filename form — the canonical, single source
// `withTheme()` rewrites from on every theme change (never rewritten from a
// previously-rewritten path, so there is no accumulation/drift risk).
const ASSET_BASE: AssetPaths = {
  logo: { hoz: '/logo-col-hoz-light.svg' },
  icon: {
    menu: '/icon-mon-fw-menu-light.svg',
    search: '/icon-mon-fw-search-light.svg',
  },
  // MARK — mixed real/placeholder imagery (see `BackgroundEntry`'s doc comment
  // above). Mike's real-asset pass (1.0.4) supplies single pre-treated
  // `bgi-<page>.webp` files (no `-light`/`-dark` token, so `withTheme()` is a
  // deliberate no-op on them; the earlier about light/dark pair is superseded)
  // for /about, /careers, /contact and /projects — all `treated: false`.
  // Companion `-alt`/`-2` variants in public/ are spares, not wired here.
  // /home, /legal and /news keep their `treated: true` scrape placeholders —
  // BackgroundLayer keeps applying its CSS grayscale/tint/darken trick to
  // those, per §5's placeholder mandate — pending real assets.
  background: {
    about: { src: '/bgi-about.webp', treated: false },
    careers: { src: '/bgi-careers.webp', treated: false },
    contact: { src: '/bgi-contact.webp', treated: false },
    home: { src: '/scrape/home/OuterCircleCOMPLETE05676Web2_landscape_large.jpg', treated: true },
    legal: { src: '/scrape/privacy-cookies-terms-conditions/Ironside_Large_Carousel_16x9_Minerals_17_landscape_large.jpg', treated: true },
    news: { src: '/scrape/home/Ironside_Small_Carousel_16x9_EC_Mono_96_landscape_small.jpg', treated: true },
    projects: { src: '/bgi-projects.webp', treated: false },
  },
};

// MARK — placeholder palettes. tokens.css carries the same caveat: the brand's real
// light/dark colour tokens have not been derived yet (1.0.2's design pass). These are
// a working default (light roughly matching the current tokens.css values; dark a
// readable inverse) so nothing here has to be un-decided later — not a design sign-off.
const PALETTE_LIGHT: Palette = { ink: '#111111', paper: '#ffffff', muted: '#525864', line: '#e5e7eb' };
const PALETTE_DARK: Palette = { ink: '#f5f5f5', paper: '#0b0b0c', muted: '#9ca3af', line: '#27272a' };

function paletteFor (theme: Theme): Palette {
  return theme === 'light' ? PALETTE_LIGHT : PALETTE_DARK;
}

// Only a structured `-dark`/`-light` token immediately before the extension is ever
// rewritten — e.g. `logo-col-hoz-light.svg` -> `logo-col-hoz-dark.svg`. Deliberately
// NOT a bare `/dark|light/` replace: content paths such as
// `FirstLightInteriorWeb2_landscape_large.jpg` (the "First Light" project) contain
// "Light" as ordinary text, nowhere near a file extension, and must pass through
// untouched.
const THEME_TOKEN_RE = /-(?:dark|light)(\.[a-z0-9]+)$/i;

export function withTheme (path: string, theme: Theme): string {
  return THEME_TOKEN_RE.test(path) ? path.replace(THEME_TOKEN_RE, `-${theme}$1`) : path;
}

type AssetTree = { [key: string]: string | AssetTree };

function mapAssetTree (tree: AssetTree, fn: (path: string) => string): AssetTree {
  const result: AssetTree = {};
  for (const [key, value] of Object.entries(tree)) {
    result[key] = typeof value === 'string' ? fn(value) : mapAssetTree(value, fn);
  }
  return result;
}

/** `BackgroundPaths`' leaves are `{ src, treated }` objects, not bare strings —
 * `mapAssetTree` above only knows string-or-nested-tree leaves, so `background`
 * is rewritten here instead of through it. `treated` is copied through
 * untouched; only `src` is theme-rewritten, exactly as every other asset path
 * is. */
function mapBackgroundPaths (paths: BackgroundPaths, theme: Theme): BackgroundPaths {
  const result = {} as BackgroundPaths;
  for (const key of Object.keys(paths) as (keyof BackgroundPaths)[]) {
    result[key] = { ...paths[key], src: withTheme(paths[key].src, theme) };
  }
  return result;
}

function assetsFor (theme: Theme): AssetPaths {
  const { logo, icon, background } = ASSET_BASE;
  // `{ logo, icon }` is a fixed-shape AssetTree (no index signature, deliberately
  // — every consumer accesses known keys like `cfg.assets.logo.hoz`, not an
  // arbitrary map) — mapAssetTree only rewrites the string leaves, so the shape
  // is preserved exactly.
  const rewritten = mapAssetTree({ logo, icon }, (path) => withTheme(path, theme));
  return { ...(rewritten as unknown as Omit<AssetPaths, 'background'>), background: mapBackgroundPaths(background, theme) };
}

function getBreakpoint (breakpoints: Record<Breakpoint, number>, width: number): Breakpoint {
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  return 'sm';
}

function getCapabilities (): Capabilities {
  return { touch: navigator.maxTouchPoints > 0 };
}

const MOBILE_UA_RE = /android|iphone|ipad|ipod|mobi/i;

function getNavigatorInfo (): NavigatorInfo {
  return {
    userAgent: navigator.userAgent,
    // MARK — deliberately minimal vs WOMBAT's fingerprint set (userAgentData
    // brands/high-entropy/vendor). This is a public marketing site, not a
    // capability-gated native/PWA surface — nothing here reads that data. Flagging
    // in case a future feature (e.g. analytics) wants it reinstated.
    platform: navigator.platform || 'web',
    mobile: MOBILE_UA_RE.test(navigator.userAgent),
  };
}

function getTimezone (): Locale['timezone'] {
  try {
    return {
      name: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // Date#getTimezoneOffset() is minutes WEST of UTC (AEST -> -600) — negated so
      // `offset` reads as the conventional minutes EAST of UTC (AEST -> +600).
      offset: -new Date().getTimezoneOffset(),
    };
  } catch {
    return { name: 'UTC', offset: 0 };
  }
}

function getViewport (breakpoints: Record<Breakpoint, number>): Viewport {
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    breakpoint: getBreakpoint(breakpoints, width),
    orientation: width >= height ? 'landscape' : 'portrait',
    touch: navigator.maxTouchPoints > 0,
  };
}

const THEME_COOKIE = 'theme';

function readThemeCookie (): Theme | null {
  const match = /(?:^|;\s*)theme=(light|dark)(?:;|$)/.exec(document.cookie);
  return match ? (match[1] as Theme) : null;
}

function persistThemeCookie (theme: Theme): void {
  const oneYearSeconds = 60 * 60 * 24 * 365;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=${oneYearSeconds}; SameSite=Lax${secure}`;
}

// Resolution order: explicit user cookie -> the `data-theme` attribute already on
// <html> (set by the inline script in index.html before this bundle ever runs, so
// there is no theme flash on load) -> the OS/browser's prefers-color-scheme -> the
// site's design default. tokens.css's current palette is light-first, so light wins
// when nothing else says otherwise.
function resolveInitialTheme (): Theme {
  const fromCookie = readThemeCookie();
  if (fromCookie) return fromCookie;

  const fromDom = document.documentElement.getAttribute('data-theme');
  if (fromDom === 'light' || fromDom === 'dark') return fromDom;

  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';

  return 'light';
}

function buildConfig (theme: Theme): AppConfig {
  return {
    api: {
      base: cfg.api.base,
      url: window.location.origin,
      timeout: cfg.api.timeout_ms,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    },
    app: {
      displayName: 'IRONSIDE',
      legalName: 'IRONSIDE Construction Pty Ltd',
      // Verbatim from index.html's <meta name="description"> — one description, not
      // a second copy invented for this file.
      tagline: 'IRONSIDE is a leading multi-residential and commercial construction business based in South Melbourne, Victoria.',
      // Verbatim from components/chrome/Footer.tsx — do not diverge from that source
      // of truth without a corresponding content update to both places.
      contact: {
        email: 'info@ironside.com.au',
        phone: '+61 3 9068 3000',
        address: '204 York Street, South Melbourne, Victoria 3205',
      },
      legal: { privacyCookiesTerms: '/privacy-cookies-terms-conditions' },
      copyright: `© ${new Date().getFullYear()} IRONSIDE Construction Pty Ltd. All rights reserved.`,
    },
    assets: assetsFor(theme),
    client: {
      base: '',
      breakpoints: BREAKPOINTS,
      capabilities: getCapabilities(),
      locale: { language: navigator.language || 'en-AU', timezone: getTimezone() },
      navigator: getNavigatorInfo(),
      theme,
      // NOT wired to document.title — hooks/usePageTitle.ts already owns that,
      // per route. This is a static descriptive title for consumers that want one
      // (e.g. future SEO/meta components), not a second place setting the tab title.
      title: 'IRONSIDE Construction',
      viewport: getViewport(BREAKPOINTS),
    },
    styles: { palette: paletteFor(theme) },
  };
}

/** Computed once at module load — mirrors WOMBAT's `cfgStatic`. Safe here because
 * this module only ever runs in the browser (no SSR in this app). */
export const cfgStatic: AppConfig = buildConfig(resolveInitialTheme());

interface ConfigContextValue {
  cfg: AppConfig;
  /** Generic setter for the rare case something outside theme/viewport needs to
   * override a top-level slice. Every call re-derives capabilities/navigator/
   * viewport/assets/palette from the resulting theme — never left stale. */
  setCfg: <K extends keyof AppConfig>(key: K, value: AppConfig[K]) => void;
  setTheme: (theme: Theme) => void;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

const RESIZE_DEBOUNCE_MS = 150;

export interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider ({ children }: ConfigProviderProps) {
  // A lazy initialiser, NOT the frozen `cfgStatic` — this app only ever mounts one
  // <ConfigProvider> per page load in production, so in practice the two coincide,
  // but resolving at mount time (rather than reusing a value resolved once at
  // module-import time) is what makes the cookie/attribute/prefers-color-scheme
  // fallback chain testable across multiple renders within one test file/module
  // instance, and is the more correct behaviour regardless.
  const [state, setState] = useState<AppConfig>(() => buildConfig(resolveInitialTheme()));

  // Not reactive by design — gates whether the prefers-color-scheme subscription
  // below is still allowed to move the theme. Starts true when a cookie already
  // exists (an explicit choice from a previous visit); becomes true the moment
  // setTheme() is called this visit.
  const explicitThemeRef = useRef<boolean>(readThemeCookie() !== null);

  const setCfg = useCallback(<K extends keyof AppConfig>(key: K, value: AppConfig[K]) => {
    setState((prev) => {
      const next: AppConfig = { ...prev, [key]: value };
      next.client = {
        ...next.client,
        capabilities: getCapabilities(),
        navigator: getNavigatorInfo(),
        viewport: getViewport(next.client.breakpoints),
      };
      next.assets = assetsFor(next.client.theme);
      next.styles = { palette: paletteFor(next.client.theme) };
      return next;
    });
  }, []);

  const setTheme = useCallback((theme: Theme) => {
    explicitThemeRef.current = true;
    persistThemeCookie(theme);
    setState((prev) => ({
      ...prev,
      assets: assetsFor(theme),
      styles: { palette: paletteFor(theme) },
      client: {
        ...prev.client,
        theme,
        capabilities: getCapabilities(),
        navigator: getNavigatorInfo(),
        viewport: getViewport(prev.client.breakpoints),
      },
    }));
  }, []);

  // DOM sync — genuine external-system effects, not a data-flow crutch. `<html>`'s
  // `data-theme` and the theme-colour meta tag live outside React's tree; tokens.css's
  // `[data-theme='dark']` block is what actually swaps the CSS custom properties that
  // Tailwind's utilities read, so this is the one place doing that write.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', state.client.theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', state.styles.palette.paper);
  }, [state.client.theme, state.styles.palette.paper]);

  // Viewport/breakpoint refresh on resize, debounced so a drag-resize doesn't
  // re-render on every pixel.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(() => {
        setState((prev) => ({ ...prev, client: { ...prev.client, viewport: getViewport(prev.client.breakpoints) } }));
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (timer !== undefined) clearTimeout(timer);
    };
  }, []);

  // Follows the OS/browser scheme change ONLY while the visitor has not made an
  // explicit choice this visit or a previous one (cookie unset) — an explicit
  // setTheme() call permanently opts this session out of that.
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      if (explicitThemeRef.current) return;
      const theme: Theme = event.matches ? 'dark' : 'light';
      setState((prev) => ({
        ...prev,
        assets: assetsFor(theme),
        styles: { palette: paletteFor(theme) },
        client: { ...prev.client, theme },
      }));
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const value = useMemo(() => ({ cfg: state, setCfg, setTheme }), [state, setCfg, setTheme]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

/** Read the config minted by the nearest <ConfigProvider> (mounted once, in
 * App.tsx). Throws if called outside one — a missing provider is a wiring bug worth
 * failing loudly on, not defaulting past (same convention as hooks/useSession.tsx). */
export function useConfig (): ConfigContextValue {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig() must be used within <ConfigProvider>');
  return ctx;
}
